import { useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import type { Message } from '../../types/message';

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  onPlayAudio?: (messageId: string) => void;
  onRetry?: () => void;
  isCollapsed?: boolean;
}

export default function MessageList({ messages, isTyping, onPlayAudio, onRetry, isCollapsed }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  if (messages.length === 0) {
    return (
      <div className={`overflow-auto flex-1 flex items-center justify-center p-8 ${isCollapsed ? 'hidden' : ''}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">👋</span>
          </div>
          <h3 className="text-lg font-semibold text-base-content mb-2">
            ¡Hola! Soy tu AI Avatar
          </h3>
          <p className="text-sm text-base-content/60 max-w-xs">
            Escribe un mensaje o usa el botón de micrófono para hablar conmigo.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 min-h-0 overflow-y-auto p-4 space-y-2 scrollbar-thin ${isCollapsed ? 'hidden' : ''}`}>
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;
        const showRetry = onRetry && message.type === 'system' && isLastMessage;
        return (
          <MessageBubble
            key={message.id}
            message={message}
            onPlayAudio={() => onPlayAudio?.(message.id)}
            onRetry={showRetry ? onRetry : undefined}
          />
        );
      })}
      
      {isTyping && (
        <div className="flex items-center gap-2 text-base-content/50">
          <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
            <span className="text-sm">🤖</span>
          </div>
          <div className="flex gap-1">
            <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      )}
      
      <div ref={bottomRef} />
    </div>
  );
}